import React from 'react';
import { cx } from './cx';

/** A small dot that breathes: something is being made right now. */
export const Pulse: React.FC<{ className?: string; small?: boolean }> = ({ className, small }) => (
  <span aria-hidden="true" className={cx('inline-block shrink-0 rounded-full bg-work-dot animate-[gdPulse_1.6s_ease-in-out_infinite]', small ? 'h-1.5 w-1.5' : 'h-2 w-2', className)} />
);

export type ChipTone = 'neutral' | 'muted' | 'brand' | 'work' | 'ok' | 'okSolid' | 'scheduled' | 'warn' | 'bad' | 'onNavy';

const TONE: Record<ChipTone, string> = {
  neutral: 'bg-inset text-navy-ink',
  muted: 'bg-inset text-ink-2',
  brand: 'bg-brand-soft text-brand-ink',
  work: 'bg-work-soft text-work',
  ok: 'bg-ok-soft text-ok',
  okSolid: 'bg-ok text-card',
  scheduled: 'bg-navy-ink text-card',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  onNavy: 'bg-navy-3 text-on-navy-2',
};

export const Chip: React.FC<{ tone?: ChipTone; children: React.ReactNode; className?: string; title?: string; pulse?: boolean }> = ({
  tone = 'neutral', children, className, title, pulse,
}) => (
  <span title={title} className={cx('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold leading-none', TONE[tone], className)}>
    {pulse && <Pulse small />}
    {children}
  </span>
);

/**
 * Campaign and brief states. Status is always a word, never colour alone.
 * `detail` adds to the word, e.g. "Scheduled · 18 Oct 9:00".
 */
export type DeskStatus =
  | 'planned' | 'waiting' | 'in_production' | 'ready' | 'approved' | 'scheduled' | 'live' | 'ended'
  | 'needs_you' | 'out_of_date' | 'stopped' | 'paused' | 'draft'
  | 'suggested' | 'brief_approved' | 'skipped' | 'has_campaign' | 'changed';

export const STATUS: Record<DeskStatus, { label: string; tone: ChipTone; mark?: string; pulse?: boolean }> = {
  planned: { label: 'Planned', tone: 'neutral' },
  waiting: { label: 'Waiting', tone: 'muted' },
  in_production: { label: 'In production', tone: 'work', pulse: true },
  ready: { label: 'Ready for review', tone: 'brand' },
  approved: { label: 'Approved', tone: 'ok', mark: '✓' },
  scheduled: { label: 'Scheduled', tone: 'scheduled' },
  live: { label: 'Live', tone: 'okSolid', mark: '●' },
  ended: { label: 'Ended', tone: 'muted' },
  needs_you: { label: 'Needs you', tone: 'bad', mark: '!' },
  out_of_date: { label: 'Out of date', tone: 'warn' },
  stopped: { label: 'Stopped · retry', tone: 'bad' },
  paused: { label: 'Paused', tone: 'warn' },
  draft: { label: 'Draft', tone: 'neutral' },
  suggested: { label: 'Suggested', tone: 'brand' },
  brief_approved: { label: 'Approved', tone: 'ok' },
  skipped: { label: 'Skipped', tone: 'muted' },
  has_campaign: { label: 'Has a campaign', tone: 'neutral' },
  changed: { label: 'Changed since its campaign', tone: 'warn' },
};

export const StatusChip: React.FC<{ status: DeskStatus; detail?: string; label?: string; className?: string }> = ({ status, detail, label, className }) => {
  const s = STATUS[status];
  return (
    <Chip tone={s.tone} pulse={s.pulse} className={className}>
      {s.mark && <span aria-hidden="true">{s.mark}</span>}
      {label ?? s.label}{detail ? ` · ${detail}` : ''}
    </Chip>
  );
};
